/*
 * @Author: Claude
 * @Date: 2025-11-15
 * @Description: 分辨率解析工具
 */
import { ResolutionPreset, RESOLUTIONS } from '../types/validator';

export interface Resolution {
  width: number;
  height: number;
}

/**
 * 判断分辨率字符串是否为预设值
 */
export const isResolutionPreset = (
  resolution: string,
): resolution is ResolutionPreset => {
  const upperResolution = resolution.toUpperCase() as ResolutionPreset;
  return upperResolution in RESOLUTIONS;
};

/**
 * 将宽高归一化为长边/短边，便于横竖屏统一比较
 */
export const normalizeResolutionDimensions = (resolution: Resolution) => ({
  longEdge: Math.max(resolution.width, resolution.height),
  shortEdge: Math.min(resolution.width, resolution.height),
});

/**
 * 解析分辨率字符串
 * @param resolution - 分辨率字符串，支持 '480P'、'720P' 或 '300*300' 格式
 * @returns Resolution - 解析后的宽高对象，解析失败返回 null
 */
export const parseResolution = (resolution: string): Resolution | null => {
  if (!resolution) {
    return null;
  }

  // 1. 尝试作为预设值解析
  const upperResolution = resolution.toUpperCase() as ResolutionPreset;
  if (isResolutionPreset(upperResolution)) {
    return RESOLUTIONS[upperResolution];
  }

  // 2. 尝试解析 '宽*高' 格式
  const match = resolution.match(/^(\d+)\s*[x*×]\s*(\d+)$/i);
  if (match) {
    const width = parseInt(match[1], 10);
    const height = parseInt(match[2], 10);
    if (width > 0 && height > 0) {
      return { width, height };
    }
  }

  return null;
};
